import type { Dispatch, SetStateAction } from 'react';
import { useRef, useState } from 'react';
import { message, Modal } from 'antd';
import { mergeProductSelection } from './shellShipmentHelpers';
import type { SensorFormRow } from './sensorFormPayload';
import {
  formatSensorList,
  productsMissingFromShipment,
  sensorsUsingProducts,
  withoutProducts,
} from './sensorProductLinks';

interface ShipmentProduct {
  id: string;
  quantity: number;
  serial_number?: string;
}

const NO_ROWS: SensorFormRow[] = [];

interface NamedProduct {
  id: string;
  name: string;
}

export interface UseSensorProductLinksOptions {
  selectedProducts: ShipmentProduct[];
  setSelectedProducts: Dispatch<SetStateAction<ShipmentProduct[]>>;
  getProductName: (id: string) => string;
  mergeIntoLookup: (products: NamedProduct[]) => void;
  loadCatalog: () => Promise<NamedProduct[] | undefined>;
  openProductForm: () => void;
  initialSensorRows?: SensorFormRow[];
  sessionKey?: string;
}

/** Sensor rows and the rules that keep their product picks and the shipment's products in step (spec UP5–UP8). */
export function useSensorProductLinks({
  selectedProducts,
  setSelectedProducts,
  getProductName,
  mergeIntoLookup,
  loadCatalog,
  openProductForm,
  initialSensorRows = NO_ROWS,
  sessionKey,
}: UseSensorProductLinksOptions) {
  const [editedRows, setEditedRows] = useState<SensorFormRow[] | null>(null);
  const [sensorCardOpen, setSensorCardOpen] = useState(false);
  const [session, setSession] = useState(sessionKey);
  if (session !== sessionKey) {
    setSession(sessionKey);
    setEditedRows(null);
    setSensorCardOpen(false);
  }
  const sensorRows = editedRows ?? initialSensorRows;
  const setSensorRows: Dispatch<SetStateAction<SensorFormRow[]>> = (action) => {
    setEditedRows((prev) =>
      typeof action === 'function' ? action(prev ?? initialSensorRows) : action
    );
  };
  const addProductTargetRef = useRef<((productId: string) => void) | null>(
    null
  );

  const changeSensorRows = (rows: SensorFormRow[]): void => {
    setSensorRows(rows);
    setSelectedProducts((prev) => {
      const missing = productsMissingFromShipment(
        rows,
        prev.map((p) => p.id)
      );
      return missing.length === 0
        ? prev
        : [...prev, ...missing.map((id) => ({ id, quantity: 1 }))];
    });
  };

  const changeShipmentProducts = (nextIds: string[]): void => {
    const removed = selectedProducts
      .map((p) => p.id)
      .filter((id) => !nextIds.includes(id));
    if (sensorCardOpen && removed.length > 0) {
      message.info('Save or cancel the open sensor before removing products');
      return;
    }
    const affected = sensorsUsingProducts(sensorRows, removed);
    const commit = (): void => {
      setSelectedProducts((prev) => mergeProductSelection(nextIds, prev));
      if (affected.length > 0) {
        setSensorRows((prev) => withoutProducts(prev, removed));
      }
    };
    const removedLabel =
      removed.length === 1
        ? getProductName(removed[0]) || 'Product'
        : `${removed.length} products`;
    if (affected.length === 0) {
      commit();
      return;
    }
    if (affected.length === 1) {
      commit();
      message.info(`${removedLabel} removed from sensor ${affected[0]}`);
      return;
    }
    Modal.confirm({
      title:
        nextIds.length === 0 && removed.length > 1
          ? 'Remove all products?'
          : `Remove ${removedLabel}?`,
      content:
        removed.length === 1
          ? `It's linked to ${affected.length} sensors (${formatSensorList(affected)}) and will be removed from all of them.`
          : `${removed.length} products are linked to ${affected.length} sensors (${formatSensorList(affected)}) and will be removed from all of them.`,
      okText: 'Remove',
      cancelText: 'Cancel',
      onOk: commit,
    });
  };

  const loadAllProductIds = async (): Promise<string[]> => {
    const products = (await loadCatalog()) ?? [];
    mergeIntoLookup(products);
    return products.map((p) => p.id);
  };

  const openAddProduct = (onCreated: (productId: string) => void): void => {
    addProductTargetRef.current = onCreated;
    openProductForm();
  };

  const finishAddProduct = (created?: NamedProduct): void => {
    if (created) {
      mergeIntoLookup([created]);
      addProductTargetRef.current?.(created.id);
    }
    addProductTargetRef.current = null;
  };

  return {
    sensorRows,
    sensorRowsEdited: editedRows !== null,
    sensorCardOpen,
    setSensorCardOpen,
    changeSensorRows,
    changeShipmentProducts,
    loadAllProductIds,
    openAddProduct,
    finishAddProduct,
  };
}
